<script setup lang="ts">
  import type { HTMLAttributes } from "vue";
  import { ref } from "vue";
  import { useVModel } from "@vueuse/core";
  import { File as FileIcon, Upload, X } from "lucide-vue-next";
  import { cn } from "@/lib/utils";

  const props = withDefaults(
    defineProps<{
      modelValue?: File | null;
      accept?: string;
      hint?: string;
      maxSize?: number;
      disabled?: boolean;
      class?: HTMLAttributes["class"];
    }>(),
    {
      modelValue: null,
      accept: "",
      hint: "",
      maxSize: 0,
      disabled: false,
      class: undefined,
    },
  );

  const emit = defineEmits<{
    "update:modelValue": [value: File | null];
  }>();

  const file = useVModel(props, "modelValue", emit, { passive: true });
  const fileInput = ref<HTMLInputElement | null>(null);
  const isDragging = ref(false);
  const localError = ref("");

  const parseAcceptList = (accept: string) =>
    accept
      .split(",")
      .map((part) => part.trim().toLowerCase())
      .filter(Boolean);

  const isFileAccepted = (candidate: File) => {
    const acceptList = parseAcceptList(props.accept);
    if (!acceptList.length) return true;
    const name = candidate.name.toLowerCase();
    const mime = candidate.type.toLowerCase();
    return acceptList.some((rule) => {
      if (rule.startsWith(".")) return name.endsWith(rule);
      if (rule.endsWith("/*")) return mime.startsWith(rule.slice(0, -1));
      return mime === rule;
    });
  };

  const setFile = (candidate: File | null) => {
    if (!candidate) {
      file.value = null;
      localError.value = "";
      return;
    }
    if (!isFileAccepted(candidate)) {
      file.value = null;
      localError.value = props.hint
        ? `Unsupported file type — ${props.hint}`
        : "Unsupported file type";
      return;
    }
    if (props.maxSize > 0 && candidate.size > props.maxSize) {
      file.value = null;
      localError.value = `File too large — maximum size is ${formatSize(props.maxSize)}`;
      return;
    }
    localError.value = "";
    file.value = candidate;
  };

  const openPicker = () => {
    if (props.disabled) return;
    fileInput.value?.click();
  };

  const onPick = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const picked = target.files?.[0];
    if (picked) setFile(picked);
    target.value = "";
  };

  const onDragOver = (event: DragEvent) => {
    event.preventDefault();
    if (props.disabled) return;
    isDragging.value = true;
  };

  const onDragLeave = () => {
    isDragging.value = false;
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    isDragging.value = false;
    if (props.disabled) return;
    const dropped = event.dataTransfer?.files?.[0];
    if (dropped) setFile(dropped);
  };

  const clear = (event: MouseEvent) => {
    event.stopPropagation();
    setFile(null);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };
</script>

<template>
  <div
    data-slot="file-dropzone"
    role="button"
    tabindex="0"
    :class="
      cn(
        'min-w-0 rounded-md border bg-muted/30 text-sm transition-colors outline-none',
        'focus-visible:ring-1 focus-visible:ring-ring',
        file
          ? 'border-solid border-input px-4 py-3'
          : 'border-2 border-dashed border-input flex flex-col items-center justify-center gap-2 px-6 py-10 text-center',
        !props.disabled && 'cursor-pointer hover:border-primary/60 hover:bg-muted/50',
        isDragging && 'border-primary bg-primary/5',
        props.disabled && 'opacity-60 cursor-not-allowed',
        props.class,
      )
    "
    @click="openPicker"
    @keydown.enter.prevent="openPicker"
    @keydown.space.prevent="openPicker"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop">
    <input
      ref="fileInput"
      type="file"
      class="hidden"
      :accept="props.accept"
      :disabled="props.disabled"
      @change="onPick" />
    <template v-if="file">
      <div class="flex items-center gap-3">
        <FileIcon class="h-6 w-6 shrink-0 text-status-success-strong" />
        <div class="flex min-w-0 flex-1 flex-col">
          <span class="truncate text-sm font-medium text-foreground">{{ file.name }}</span>
          <span class="text-xs text-muted-foreground">{{ formatSize(file.size) }}</span>
        </div>
        <button
          type="button"
          class="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          @click="clear">
          <X class="h-4 w-4" />
        </button>
      </div>
    </template>
    <template v-else>
      <Upload class="h-5 w-5 text-muted-foreground" />
      <span class="text-muted-foreground">Drag your file here or click to select</span>
      <span
        v-if="props.hint"
        class="text-xs text-muted-foreground">
        {{ props.hint }}
      </span>
      <span
        v-if="localError"
        class="text-xs text-destructive">
        {{ localError }}
      </span>
    </template>
  </div>
</template>
